/**
 * Per-frame preview dispatcher for pose-mode bone commands (FK rotate +
 * IK translate). Called from commandApplyLive when the active command is
 * `boneRotate` or `boneTranslate`.
 *
 * Three paths land here:
 *  1. FK rotate (`boneRotate`)   → solveBoneRotate → mannequinBonePoses patch
 *  2. Root-bone translate        → scene object position patch (no IK)
 *  3. IK chain translate         → dispatched to the IK worker when ready,
 *                                   otherwise solved on the main thread via
 *                                   solveBoneTranslate.
 *
 * Whole-body "body grab" drags (captured in enterCommand as auto-pins) are
 * routed through dispatchWholeBodySolve here.
 */

import * as THREE from "three/webgpu";
import { getBoneMap, getRestPoses } from "../../mannequin/boneRegistry.ts";
import type { IKChainState } from "../../mannequin/ikChainState.ts";
import {
  buildSkeletonStateCore,
  chainStateToCore,
  collectBonePoses,
  vec3ToPlain,
} from "../../mannequin/ikWorkerBridge.ts";
import {
  consumeLatestResult,
  dispatchChainSolve,
  dispatchWholeBodySolve,
  isIKWorkerReady,
} from "../../mannequin/ikWorkerClient.ts";
import {
  solveBoneRotate,
  solveBoneTranslate,
} from "../../mannequin/posePipeline.ts";
import { applySnapping } from "../../modeling/snap.ts";
import { isFullSnapshot } from "../storeUndo.ts";
import type { ApplyLiveContext } from "./core.ts";
import {
  _scratchVec3A,
  _scratchVec3B,
  _scratchVec3C,
  _scratchVec3D,
} from "./core.ts";
import { visibleHeight } from "./transformMath.ts";

export function applyLiveBoneCommand(ctx: ApplyLiveContext): void {
  const {
    set,
    state,
    cb,
    snap,
    camera,
    rect,
    screenX,
    screenY,
    dx,
    dy,
    snapGridLevelOffset,
  } = ctx;
  if (!isFullSnapshot(snap)) return;
  const pm = state.poseMode;
  if (!pm?.objectId || !pm?.activeBone || !cb.boneCommandData) return;
  const pmObjectId = pm.objectId;
  const pmActiveBone = pm.activeBone;

  const { origLocalQuat, parentWorldQuat: pwq } = cb.boneCommandData;

  if (cb.type === "boneRotate") {
    const result = solveBoneRotate({
      objectId: pmObjectId,
      activeBone: pmActiveBone,
      selectedBones: pm.selectedBones,
      origLocalQuat,
      parentWorldQuat: pwq,
      axis: cb.axis,
      normalVector: cb.normalVector,
      boneLocalAxis: cb.boneLocalAxis ?? null,
      camera,
      rect,
      screenX,
      screenY,
      ox: ctx.ox,
      oy: ctx.oy,
      snapshotBonePoses: snap.mannequinBonePoses,
      lockedBones: state.lockedBones.get(pmObjectId),
      constraintWeight: state.constraintWeights.get(pmObjectId) ?? 1,
    });
    if (result) set({ mannequinBonePoses: result.bonePoses });
    return;
  }

  // boneTranslate from here on
  const chainState = cb.boneCommandData?.ikChainState as
    | IKChainState
    | undefined;
  const origPositions = cb.boneCommandData?.ikOrigPositions as
    | THREE.Vector3[]
    | undefined;

  if (!chainState || !origPositions) {
    // Root bone has no IK chain — translate the entire scene object instead.
    const mannequinObj = state.sceneObjects.get(pmObjectId);
    const snapObj = snap.sceneObjects.find((s) => s.id === pmObjectId);
    if (!mannequinObj || !snapObj) return;

    const pivotWorld = _scratchVec3A.set(...snapObj.position);
    const dist = camera.position.distanceTo(pivotWorld);
    const vh = visibleHeight(camera, dist);
    const worldPerPx = vh / rect.height;

    const camRight = _scratchVec3C;
    const camUp = _scratchVec3D;
    camera.matrixWorld.extractBasis(camRight, camUp, _scratchVec3B);

    let moveVec: THREE.Vector3;
    if (cb.axis === "x" || cb.axis === "y" || cb.axis === "z") {
      const axisVec = new THREE.Vector3(
        cb.axis === "x" ? 1 : 0,
        cb.axis === "y" ? 1 : 0,
        cb.axis === "z" ? 1 : 0,
      );
      const axisSS = pivotWorld.clone().add(axisVec).project(camera);
      const originSS = pivotWorld.clone().project(camera);
      const axisDirSS = new THREE.Vector2(
        axisSS.x - originSS.x,
        axisSS.y - originSS.y,
      );
      if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
      const ndcDx = (dx / rect.width) * 2;
      const ndcDy = -(dy / rect.height) * 2;
      const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
      const worldDist = proj * vh;
      moveVec = axisVec.multiplyScalar(worldDist);
    } else {
      moveVec = camRight
        .clone()
        .multiplyScalar(dx * worldPerPx)
        .add(camUp.clone().multiplyScalar(-dy * worldPerPx));
    }

    applySnapping(
      state,
      screenX,
      screenY,
      rect,
      snapObj.position,
      moveVec,
      cb.axis,
      null, // Root bone translation doesn't use normal constraint currently
      new Set([pmObjectId]),
      snapGridLevelOffset,
    );

    const newMap = new Map(state.sceneObjects);
    newMap.set(pmObjectId, {
      ...mannequinObj,
      position: [
        snapObj.position[0] + moveVec.x,
        snapObj.position[1] + moveVec.y,
        snapObj.position[2] + moveVec.z,
      ] as [number, number, number],
    });
    set({ sceneObjects: newMap });
    return;
  }

  const mannequinObj = state.sceneObjects.get(pmObjectId);

  // Merge body-grab auto-pins with any existing user pins.
  let pinnedBones = state.pinnedBones.get(pmObjectId);
  const bodyGrabPins = cb.boneCommandData?.bodyGrabPins;
  if (bodyGrabPins && bodyGrabPins.size > 0) {
    const merged = new Map<string, [number, number, number]>(pinnedBones ?? []);
    for (const [name, pos] of bodyGrabPins) {
      if (!merged.has(name)) merged.set(name, pos);
    }
    pinnedBones = merged;
  }

  const boneMapObj = getBoneMap(pmObjectId);
  const workerReady = isIKWorkerReady();

  // --- Worker path: dispatch non-blocking IK solve ---
  if (workerReady && boneMapObj) {
    // Apply previous frame's worker result if available
    const workerResult = consumeLatestResult();
    if (workerResult) {
      const posesPatch = new Map(state.mannequinBonePoses);
      const bonePoses = new Map(posesPatch.get(pmObjectId) ?? new Map());
      if (workerResult.bonePoses) {
        for (const [name, q] of workerResult.bonePoses) bonePoses.set(name, q);
      }
      if (workerResult.secondaryPoses) {
        for (const [name, q] of workerResult.secondaryPoses)
          bonePoses.set(name, q);
      }
      posesPatch.set(pmObjectId, bonePoses);
      set({
        mannequinBonePoses: posesPatch,
        ikDragState: workerResult.ikDragState ?? null,
      });
    }

    // Dispatch current frame's solve
    const constraintWeight = state.constraintWeights.get(pmObjectId) ?? 1;
    const isFirstFrame = !(cb as any)._workerFirstFrameSent;
    (cb as any)._workerFirstFrameSent = true;

    const rp = getRestPoses(pmObjectId);
    const currentBonePoses = collectBonePoses(
      state.mannequinBonePoses.get(pmObjectId),
      boneMapObj,
      rp as Map<string, [number, number, number, number]> | undefined,
    );

    if (pinnedBones && pinnedBones.size > 0) {
      // Whole-body solve
      const currentPoses = snap.mannequinBonePoses.get(pmObjectId);
      const skeleton = buildSkeletonStateCore(
        boneMapObj,
        currentPoses,
        undefined,
      );
      const targets: {
        boneName: string;
        position: [number, number, number];
        weight: number;
      }[] = [];

      for (const [boneName, pos] of pinnedBones) {
        if (boneName === pmActiveBone) continue;
        targets.push({ boneName, position: pos, weight: 1.0 });
      }

      // Compute target position for the drag bone
      const effectorPos = origPositions[origPositions.length - 1];
      const pivotWorld = effectorPos;
      const camDistWB = camera.position.distanceTo(pivotWorld);
      const vhWB = visibleHeight(camera, camDistWB);
      const worldPerPxWB = vhWB / rect.height;
      const camRight = new THREE.Vector3();
      const camUp = new THREE.Vector3();
      const camFwd = new THREE.Vector3();
      camera.matrixWorld.extractBasis(camRight, camUp, camFwd);
      const moveVec = camRight
        .clone()
        .multiplyScalar(dx * worldPerPxWB)
        .add(camUp.clone().multiplyScalar(-dy * worldPerPxWB));

      applySnapping(
        state,
        screenX,
        screenY,
        rect,
        effectorPos,
        moveVec,
        null, // Whole-body drag is free-form
        null,
        new Set([pmObjectId]),
        snapGridLevelOffset,
      );

      const targetPos = effectorPos.clone().add(moveVec);

      targets.push({
        boneName: pmActiveBone,
        position: vec3ToPlain(targetPos),
        weight: 1.0,
      });

      dispatchWholeBodySolve({
        skeleton,
        targets,
        constraintWeight,
        activeBone: pmActiveBone,
        lockedBones: state.lockedBones.get(pmObjectId)
          ? [...state.lockedBones.get(pmObjectId)!]
          : undefined,
        currentBonePoses,
        hasSession: true,
        isFirstFrame,
      });
    } else {
      // Chain solve
      const solverType = cb.boneCommandData?.ikSolverType ?? "jacobian";
      const ikPoleDir = cb.boneCommandData?.ikPoleDirection;
      const mannequinRotation = mannequinObj?.rotation ?? [0, 0, 0];

      // Compute target
      const effectorPos = origPositions[origPositions.length - 1];
      const camDistCS = camera.position.distanceTo(effectorPos);
      const vhCS = visibleHeight(camera, camDistCS);
      const worldPerPxCS = vhCS / rect.height;
      const camRight = new THREE.Vector3();
      const camUp = new THREE.Vector3();
      const camFwd = new THREE.Vector3();
      camera.matrixWorld.extractBasis(camRight, camUp, camFwd);

      let moveVec: THREE.Vector3;
      if (cb.axis === "x" || cb.axis === "y" || cb.axis === "z") {
        const axisVec = new THREE.Vector3(
          cb.axis === "x" ? 1 : 0,
          cb.axis === "y" ? 1 : 0,
          cb.axis === "z" ? 1 : 0,
        );
        const axisSS = effectorPos.clone().add(axisVec).project(camera);
        const originSS = effectorPos.clone().project(camera);
        const axisDirSS = new THREE.Vector2(
          axisSS.x - originSS.x,
          axisSS.y - originSS.y,
        );
        if (axisDirSS.length() > 1e-6) axisDirSS.normalize();
        const ndcDx = (dx / rect.width) * 2;
        const ndcDy = -(dy / rect.height) * 2;
        const proj = ndcDx * axisDirSS.x + ndcDy * axisDirSS.y;
        const worldDist = proj * vhCS;
        moveVec = axisVec.multiplyScalar(worldDist);
      } else {
        moveVec = camRight
          .clone()
          .multiplyScalar(dx * worldPerPxCS)
          .add(camUp.clone().multiplyScalar(-dy * worldPerPxCS));
      }

      applySnapping(
        state,
        screenX,
        screenY,
        rect,
        effectorPos,
        moveVec,
        cb.axis,
        null,
        new Set([pmObjectId]),
        snapGridLevelOffset,
      );

      const targetPos = effectorPos.clone().add(moveVec);

      let poleVec: [number, number, number] | undefined;
      if (ikPoleDir) {
        const pv = new THREE.Vector3(ikPoleDir[0], ikPoleDir[1], ikPoleDir[2]);
        const [rx, ry, rz] = mannequinRotation;
        pv.applyQuaternion(
          new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, ry, rz)),
        );
        poleVec = [pv.x, pv.y, pv.z];
      }

      // View bias for depth preservation
      let viewBias:
        | {
            forward: [number, number, number];
            origMidDepth: number;
            strength: number;
          }
        | undefined;
      camera.getWorldDirection(camFwd);
      if (solverType === "analytical") {
        viewBias = {
          forward: vec3ToPlain(camFwd),
          origMidDepth: origPositions[1].dot(camFwd),
          strength: 0.6,
        };
      } else {
        // Jacobian depth bias
        const origDepth = origPositions[origPositions.length - 1].dot(camFwd);
        const curDepth = targetPos.dot(camFwd);
        targetPos.addScaledVector(camFwd, -(curDepth - origDepth) * 0.5);
      }

      dispatchChainSolve({
        solverType,
        chain: chainStateToCore(chainState),
        target: vec3ToPlain(targetPos),
        activeBone: pmActiveBone,
        jointWeights: cb.boneCommandData?.ikJointWeights,
        constraintWeight,
        poleDirection: poleVec,
        viewBias,
        excludeLastBone: true,
        currentBonePoses,
        hasSession: true,
        isFirstFrame,
      });
    }
  } else {
    // --- Fallback: sync solve on main thread ---
    const result = solveBoneTranslate({
      state,
      objectId: pmObjectId,
      activeBone: pmActiveBone,
      chainState,
      origPositions,
      ikPoleDirection: cb.boneCommandData?.ikPoleDirection,
      ikSolverType: cb.boneCommandData?.ikSolverType ?? "jacobian",
      ikJointWeights: cb.boneCommandData?.ikJointWeights,
      mannequinRotation: mannequinObj?.rotation ?? [0, 0, 0],
      axis: cb.axis,
      camera,
      rect,
      dx,
      dy,
      screenX,
      screenY,
      snapshotBonePoses: snap.mannequinBonePoses,
      lockedBones: state.lockedBones.get(pmObjectId),
      pinnedBones,
      boneMap: boneMapObj,
      constraintWeight: state.constraintWeights.get(pmObjectId) ?? 1,
    });

    if (result) {
      set({
        mannequinBonePoses: result.bonePoses,
        ikDragState: result.ikDragState,
      });
    }
  }
}
